/**
 * Lazy-loader de ResponseGroup enfants par lot pour le comparateur.
 *
 * Probleme : dans un survey avec une question object_group ayant des centaines
 * voire milliers de RG enfants, on ne peut pas tous les charger au mount (N+1).
 * On les charge a la demande (quand l'utilisateur deplie un RG) avec un batching
 * intelligent :
 *   - debounce 200 ms pour cumuler plusieurs expansions rapprochees
 *   - flush immediat des qu'on atteint 10 uuids en attente
 *
 * API publique :
 *   rgBatchLoader.loadRg(uuid) -> Promise<void>
 *     Resout quand le RG est charge dans le formStore (ou en cas d'echec).
 *
 * Les RG charges sont stockes dans formStore via STORE_RESPONSE_GROUP + LOAD_RESPONSES,
 * donc disponibles via les getters existants (getResponseGroup, getSurveyFull).
 */

import {api} from "@/services/Api";
import store from "@/store";

interface QueueEntry {
  uuid: string;
  promise: Promise<void>;
  resolve: () => void;
}

class RgBatchLoader {
  private readonly DEBOUNCE_MS = 200;
  private readonly BATCH_MAX = 10;

  private queue: Map<string, QueueEntry> = new Map();
  private timer: number | null = null;

  loadRg(uuid: string): Promise<void> {
    if (!uuid) return Promise.resolve();
    const existing = this.queue.get(uuid);
    if (existing) return existing.promise;
    let resolveFn: () => void = () => {};
    const promise = new Promise<void>((res) => {
      resolveFn = res;
    });
    this.queue.set(uuid, {uuid, promise, resolve: resolveFn});
    if (this.queue.size >= this.BATCH_MAX) {
      this.flush();
    } else {
      this.scheduleFlush();
    }
    return promise;
  }

  private scheduleFlush() {
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => this.flush(), this.DEBOUNCE_MS);
  }

  private async flush() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.size === 0) return;
    const entries = Array.from(this.queue.values());
    const uuids = entries.map((e) => e.uuid);
    this.queue.clear();

    try {
      const res = await api.post(api.form, "api/get/rg/batch", {uuids});
      const items = res?.data?.responseGroups || [];
      const loadedUuids = new Set<string>();
      for (const item of items) {
        const rg = item?.responseGroup;
        if (!rg || !rg.uuid) continue;
        store.commit("formStore/STORE_RESPONSE_GROUP", rg);
        if (rg.responses) {
          store.commit("formStore/LOAD_RESPONSES", {
            rgUuid: rg.uuid,
            responses: rg.responses,
          });
        }
        loadedUuids.add(rg.uuid);
      }
      // Resoudre tous les promesses, meme celles des RG introuvables, pour que
      // les appelants ne bloquent pas indefiniment.
      for (const entry of entries) {
        entry.resolve();
      }
      // Log simple si certains n'ont rien renvoye (ex: supprimes, droits insuffisants).
      const missing = uuids.filter((u) => !loadedUuids.has(u));
      if (missing.length > 0) {
        console.warn("RgBatchLoader: some RG uuids were not returned by the batch endpoint", missing);
      }
    } catch (err) {
      console.error("RgBatchLoader flush failed", err);
      for (const entry of entries) {
        entry.resolve();
      }
    }
  }
}

export const rgBatchLoader = new RgBatchLoader();
